import {checkFilterQuerySync, isFilterBlock, isIndexedGroupBlock} from '@github-ui/filter-core'
import {type AnyBlock, BlockType, type FilterBlock} from '@github-ui/filter-core'
import {type ReactNode, useEffect, useMemo, useState} from 'react'

import {useFilter, useFilterQueryState, useInputDispatch, useInputState} from '../context'
import {FilterExpressionBlock} from './FilterExpressionBlock'
import {KeywordBlock, SpaceBlock, TextBlock} from './StyledInputBlocks'

export const StyledInput = () => {
  const {config} = useFilter()
  const {inputFocused, inputValue} = useInputState()
  const {updateStyledInputBlockCount} = useInputDispatch()
  const {filterQuery} = useFilterQueryState()
  const {blocks, activeBlock} = filterQuery
  const activeBlockId = (activeBlock as FilterBlock | undefined)?.id
  // State-based cache of the block count from the last successfully-rendered styled tree. Kept so
  // the reported block count stays stable during the out-of-sync window (rather than collapsing to
  // the raw-text node) while a parse/validate is still in flight. Only the count is consumed (by
  // `updateStyledInputBlockCount` below), so we cache the number rather than the node array — which
  // is also what keeps the render-phase setState guard below loop-free under react-compiler (see
  // the comment on that guard).
  const [cachedBlockCount, setCachedBlockCount] = useState(0)

  // Compare the parsed query against the synchronously-updated raw input value (which is the exact
  // same string that gets parsed, just available one commit earlier) rather than a parser-fed
  // mirror. This flips to "out of sync" the instant the user types — one commit before the parse
  // runs in an effect — so the raw-text fallback in the return below can paint the typed characters
  // immediately instead of waiting on the (potentially slow, under CPU throttling) parse.
  const inSync = checkFilterQuerySync(filterQuery, inputValue)

  const freshStyledInput = useMemo<ReactNode[] | null>(() => {
    if (!inSync) return null

    const inputParts: ReactNode[] = []

    function getStyledInputs(subBlocks: AnyBlock[], parentKey: string) {
      for (const [index, element] of subBlocks.entries()) {
        let queryItem: ReactNode

        if (isFilterBlock(element)) {
          const {key} = element
          queryItem = (
            <FilterExpressionBlock
              key={`filter-block-${parentKey}-${index}-${key.value}`}
              block={element}
              isActiveBlock={activeBlockId === element.id}
              inputFocused={inputFocused}
              filterDelimiter={config.filterDelimiter}
              valueDelimiter={config.valueDelimiter}
            />
          )
        } else if (element.type === BlockType.Space) {
          queryItem = <SpaceBlock key={`space-block-${parentKey}-${index}`} text={element.raw} />
        } else if (element.type === BlockType.Keyword) {
          queryItem = <KeywordBlock key={`keyword-block-${parentKey}-${index}`} keyword={element.raw} />
        } else if (isIndexedGroupBlock(element)) {
          inputParts.push(<TextBlock key={`text-open-${parentKey}-${index}`} text={'('} />)
          getStyledInputs(element.blocks, parentKey ? `${parentKey}-${index}` : `${index}`)
          inputParts.push(<TextBlock key={`text-close-${parentKey}-${index}`} text={')'} />)
        } else {
          queryItem = <TextBlock key={`text-block-${parentKey}-${index}`} text={element.raw} />
        }

        inputParts.push(queryItem)
      }
    }

    getStyledInputs(blocks, 'root')

    return inputParts
  }, [blocks, activeBlockId, inputFocused, config.filterDelimiter, config.valueDelimiter, inSync])

  // Update the cache when a fresh styled tree is computed. Set state during render (guarded by a
  // value comparison) is preferred over an effect: React cancels the current render and starts a
  // new one with the updated cache, avoiding the extra commit that a useEffect-based update would
  // cause. The guard compares the numeric block count, NOT the memoized `freshStyledInput` array's
  // identity: react-compiler does not preserve a useMemo result's referential identity across
  // renders, so an identity check (`freshStyledInput !== cachedNodes`) would always be true and
  // loop until React throws "Too many re-renders". A length comparison settles after one update.
  if (freshStyledInput !== null && freshStyledInput.length !== cachedBlockCount) {
    setCachedBlockCount(freshStyledInput.length)
  }

  // Block count tracks the last successfully-styled tree so layout-dependent consumers stay stable
  // during the out-of-sync window instead of seeing the single raw-text fallback node.
  const styledBlockCount = freshStyledInput?.length ?? cachedBlockCount

  useEffect(() => {
    updateStyledInputBlockCount(styledBlockCount)
  }, [styledBlockCount, updateStyledInputBlockCount])

  // When the parsed query is in sync with the latest typed value, render the styled (colorized)
  // block tree. While it is out of sync — a parse/validate is still in flight (it runs in an effect,
  // one commit behind the keystroke, and can lag further under heavy CPU load) — render the raw
  // typed text immediately so the characters the user typed are always visible without waiting on
  // the parse; styling "catches up" on the next commit. Styled value blocks add only color/background
  // (no padding or margins), so the raw text occupies identical character positions: no layout shift
  // or caret movement when it colorizes.
  return <>{freshStyledInput ?? inputValue}</>
}
